'use client';

import { TBlogCategoryItemized } from '@/app/(blogposts)/blog/blog.types';
import { Text } from '@/components/ui/Text';
import clsx from 'clsx';
import { useCallback, useMemo, useState } from 'react';
import { BlogArticleCard } from '../../BlogArticleCard';
import { BlogCategorySeeMoreButton } from '../../BlogCategorySeeMoreButton';
import { BlogPagesShowOnDesktop } from '../../BlogPagesShowOnDesktop';
import { BlogPagesShowOnMobile } from '../../BlogPagesShowOnMobile';

import './BlogCategory.scss';

const DEFAULT_SHOW_NO = 3;

type Variant = 'homepage' | 'categoryPage';

type Props = {
  category: TBlogCategoryItemized;
  variant?: Variant;
  className?: string;
};

export const BlogCategory: React.FC<Props> = ({
  category: {
    item: { articles: _articles, title, categorySlug },
  },
  variant = 'homepage',
  className,
}) => {
  const [numberOfItems, setNumberOfItems] = useState(
    variant === 'homepage' ? DEFAULT_SHOW_NO : Number.MAX_SAFE_INTEGER
  );

  const handleClick = useCallback(
    () => setNumberOfItems(prevValue => prevValue + DEFAULT_SHOW_NO),
    []
  );

  const articles = useMemo(
    () => _articles.slice(0, numberOfItems),
    [_articles, numberOfItems]
  );

  const allItemsShown = numberOfItems >= _articles.length;

  return (
    <div className={clsx('BlogCategory', className)}>
      <a
        href={
          variant === 'homepage' ? `/blog/category/${categorySlug}` : undefined
        }
      >
        <Text variant='h3'>{title}</Text>
      </a>

      <hr className='BlogCategory__separator' />

      <BlogPagesShowOnDesktop>
        <div className='BlogCategory__articles-container BlogCategory__articles-container--desktop'>
          {articles.map((article, index) => (
            <BlogArticleCard
              key={`${article.item.articleId} - ${index} - desktop`}
              article={article}
            />
          ))}
        </div>
      </BlogPagesShowOnDesktop>

      <BlogPagesShowOnMobile>
        <div className='BlogCategory__articles-container BlogCategory__articles-container--mobile'>
          {articles.map((article, index) => (
            <BlogArticleCard
              key={`${article.item.articleId} - ${index} - mobile`}
              article={article}
            />
          ))}
        </div>
      </BlogPagesShowOnMobile>

      {variant === 'homepage' && !allItemsShown && (
        <BlogCategorySeeMoreButton
          className='BlogCategory__cta'
          onClick={handleClick}
        />
      )}
    </div>
  );
};
